import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export default function DepositPage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('account_summary')
        .select('available_margin')
        .eq('user_id', user.id);
      const total = (data || []).reduce((sum, row) => sum + Number(row.available_margin || 0), 0);
      setBalance(total);
    } catch {
      setBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  return (
    <div className="min-h-screen bg-brown-50 pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">选择入金方式</span>
        <div className="w-8" />
      </div>

      {/* 余额卡片 */}
      <div className="mx-3 mt-3 bg-white rounded-2xl border border-gold-100 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-brown-500 text-xs">账户可用余额</p>
            <p className="text-brown-900 text-xl font-bold mt-0.5">
              {balanceLoading ? (
                <span className="inline-block w-20 h-5 bg-brown-100 rounded animate-pulse" />
              ) : (
                `¥${balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
              )}
            </p>
          </div>
          <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center">
            <span className="w-5 h-5 flex items-center justify-center text-gold-500">
              <i className="ri-wallet-3-line text-sm"></i>
            </span>
          </div>
        </div>
      </div>

      {/* 副标题区 */}
      <div className="px-4 pt-5 pb-3 text-center">
        <div className="flex items-center justify-center gap-3">
          <span className="h-px w-8 bg-gold-300/40"></span>
          <p className="text-brown-700 text-sm">请选择以下任一方式完成资金/资产转入</p>
          <span className="h-px w-8 bg-gold-300/40"></span>
        </div>
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className="w-4 h-4 flex items-center justify-center text-gold-500">
            <i className="ri-shield-check-line text-xs"></i>
          </span>
          <p className="text-brown-500 text-xs">安全合规 · 资金保障 · 专业服务</p>
        </div>
      </div>

      <div className="px-3 space-y-3">
        {/* USDT入金卡片（推荐） */}
        <div className="bg-white rounded-2xl border border-gold-100 overflow-hidden">
          {/* 推荐标签 */}
          <div className="relative">
            <div className="absolute top-0 left-0 bg-gradient-to-r from-gold-400 to-gold-500 text-brown-950 text-[10px] font-bold px-3 py-1 rounded-br-lg flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-star-fill text-[8px]"></i>
              </span>
              推荐
            </div>
          </div>

          <div className="p-4 pt-7">
            <div className="flex items-start gap-3">
              {/* USDT标志 */}
              <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl font-bold tracking-tight">T</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-brown-900 text-lg font-bold">USDT入金</h3>
                <p className="text-brown-400 text-xs mt-0.5">推荐使用</p>
              </div>
              <span className="w-5 h-5 flex items-center justify-center text-brown-300 mt-1">
                <i className="ri-arrow-right-s-line text-lg"></i>
              </span>
            </div>

            {/* 三个特点 */}
            <div className="flex items-start gap-3 mt-3 px-1">
              <div className="flex-1 text-center">
                <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-1">
                  <span className="w-4 h-4 flex items-center justify-center text-amber-500">
                    <i className="ri-flashlight-line text-xs"></i>
                  </span>
                </div>
                <p className="text-brown-700 text-xs font-medium">快速到账</p>
                <p className="text-brown-400 text-[10px] mt-0.5">通常10~30分钟</p>
              </div>
              <div className="flex-1 text-center">
                <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-1">
                  <span className="w-4 h-4 flex items-center justify-center text-amber-500">
                    <i className="ri-global-line text-xs"></i>
                  </span>
                </div>
                <p className="text-brown-700 text-xs font-medium">全球通用</p>
                <p className="text-brown-400 text-[10px] mt-0.5">7x24小时可用</p>
              </div>
              <div className="flex-1 text-center">
                <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-1">
                  <span className="w-4 h-4 flex items-center justify-center text-amber-500">
                    <i className="ri-shield-check-line text-xs"></i>
                  </span>
                </div>
                <p className="text-brown-700 text-xs font-medium">安全稳定</p>
                <p className="text-brown-400 text-[10px] mt-0.5">链上确认保障</p>
              </div>
            </div>

            {/* 选择按钮 */}
            <button
              onClick={() => navigate('/deposit/usdt')}
              className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 text-white text-sm font-semibold cursor-pointer flex items-center justify-center gap-1 active:scale-[0.98] transition-transform"
            >
              选择 USDT 入金
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm"></i>
              </span>
            </button>
          </div>

          {/* 底部说明 */}
          <div className="px-4 py-2.5 border-t border-gold-50 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center text-brown-400">
                <i className="ri-information-line text-xs"></i>
              </span>
              <p className="text-brown-400 text-xs">支持主流公链：TRC20（推荐）/ ERC20 / BEP20</p>
            </div>
            <button className="flex items-center gap-0.5 text-gold-500 text-xs cursor-pointer whitespace-nowrap">
              查看转入说明
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
            </button>
          </div>
        </div>

        {/* 以料入金卡片 */}
        <div className="bg-white rounded-2xl border border-gold-100 overflow-hidden">
          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* 金条图 */}
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Shiny%20pure%20gold%20bar%20ingot%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20warm%20golden%20metallic%20surface%2C%20minimalist%20elegant%20style&width=120&height=120&seq=deposit-gold-bar-01&orientation=squarish"
                  alt="金条"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-brown-900 text-lg font-bold">以料入金</h3>
                <p className="text-brown-400 text-xs mt-0.5">实物黄金板料入金</p>
              </div>
              <span className="w-5 h-5 flex items-center justify-center text-brown-300 mt-1">
                <i className="ri-arrow-right-s-line text-lg"></i>
              </span>
            </div>

            {/* 流程 */}
            <div className="flex items-center justify-center gap-1 mt-4 px-2">
              <div className="text-center flex-1">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-1">
                  <span className="w-5 h-5 flex items-center justify-center text-amber-500">
                    <i className="ri-truck-line text-sm"></i>
                  </span>
                </div>
                <p className="text-brown-700 text-[10px] font-medium">1. 交付黄金</p>
                <p className="text-brown-400 text-[9px] mt-0.5">按要求交付板料</p>
              </div>
              <span className="w-4 h-4 flex items-center justify-center text-brown-300 mb-4">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
              <div className="text-center flex-1">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-1">
                  <span className="w-5 h-5 flex items-center justify-center text-amber-500">
                    <i className="ri-vip-diamond-line text-sm"></i>
                  </span>
                </div>
                <p className="text-brown-700 text-[10px] font-medium">2. 检测称重</p>
                <p className="text-brown-400 text-[9px] mt-0.5">检测成色与重量</p>
              </div>
              <span className="w-4 h-4 flex items-center justify-center text-brown-300 mb-4">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
              <div className="text-center flex-1">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-1">
                  <span className="w-5 h-5 flex items-center justify-center text-amber-500">
                    <i className="ri-wallet-3-line text-sm"></i>
                  </span>
                </div>
                <p className="text-brown-700 text-[10px] font-medium">3. 计价结算</p>
                <p className="text-brown-400 text-[9px] mt-0.5">确认金额入账户</p>
              </div>
            </div>

            {/* 选择按钮 */}
            <button
              onClick={() => navigate('/deposit/material')}
              className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 text-white text-sm font-semibold cursor-pointer flex items-center justify-center gap-1 active:scale-[0.98] transition-transform"
            >
              选择以料入金
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm"></i>
              </span>
            </button>
          </div>

          {/* 底部说明 */}
          <div className="px-4 py-2.5 border-t border-gold-50 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center text-brown-400">
                <i className="ri-information-line text-xs"></i>
              </span>
              <p className="text-brown-400 text-xs">支持 99.99% / 99.95% / 99.9% 等常见黄金成色</p>
            </div>
            <button className="flex items-center gap-0.5 text-gold-500 text-xs cursor-pointer whitespace-nowrap">
              查看交付方式
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
            </button>
          </div>
        </div>

        {/* 禁用卡片列表 */}
        {[
          {
            icon: 'ri-wechat-line',
            name: '微信支付',
            desc: '该入金方式暂不可用',
          },
          {
            icon: 'ri-alipay-line',
            name: '支付宝',
            desc: '该入金方式暂不可用',
          },
          {
            icon: 'ri-bank-card-line',
            name: '银行卡转账',
            desc: '该入金方式暂不可用',
          },
        ].map((item) => (
          <div
            key={item.name}
            className="bg-brown-100/60 rounded-xl px-4 py-3.5 flex items-center gap-3 opacity-70"
          >
            <div className="w-10 h-10 rounded-full bg-brown-200/60 flex items-center justify-center flex-shrink-0">
              <span className="w-5 h-5 flex items-center justify-center text-brown-400">
                <i className={`${item.icon} text-lg`}></i>
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-brown-600 text-sm font-medium">{item.name}</p>
              <p className="text-brown-400 text-xs mt-0.5">{item.desc}</p>
            </div>
            <span className="px-2 py-1 rounded-full bg-brown-200/50 text-brown-500 text-[10px] flex-shrink-0">
              暂不可用
            </span>
            <span className="w-4 h-4 flex items-center justify-center text-brown-300 flex-shrink-0">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
          </div>
        ))}
      </div>

      {/* 温馨提示 */}
      <div className="mx-3 mt-5 bg-white rounded-2xl border border-gold-100 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-4 h-4 flex items-center justify-center text-amber-500">
                <i className="ri-shield-check-line text-sm"></i>
              </span>
              <h4 className="text-brown-800 text-sm font-bold">温馨提示</h4>
            </div>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0"></span>
                <p className="text-brown-500 text-xs">请确保转入账户与您本人身份信息一致</p>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0"></span>
                <p className="text-brown-500 text-xs">入金后资金将实时进入您的账户，可用于预约交易与履约</p>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0"></span>
                <p className="text-brown-500 text-xs">如有任何问题，请联系您的专属客服</p>
              </li>
            </ul>
          </div>
          <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center">
            <img
              src="https://readdy.ai/api/search-image?query=Minimalist%20bank%20building%20icon%20with%20shield%2C%20warm%20golden%20and%20white%20color%20palette%2C%20flat%20illustration%20style%2C%20clean%20background&width=120&height=120&seq=deposit-bank-icon&orientation=squarish"
              alt="银行"
              className="w-14 h-14 object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
}